import { useState, useCallback } from 'react';
import {
  useLocalParticipant,
  useRoomContext,
  TrackToggle,
  DisconnectButton,
} from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';
import { useTranslation } from '../../hooks/useTranslation';

interface VideoCallControlsProps {
  onLeave?: () => void;
  onToggleChat?: () => void;
  onToggleTranscription?: () => void;
  showChatButton?: boolean;
  showTranscriptionButton?: boolean;
  showRecordButton?: boolean;
}

export function VideoCallControls({
  onLeave,
  onToggleChat,
  onToggleTranscription,
  showChatButton = true,
  showTranscriptionButton = true,
  showRecordButton = false,
}: VideoCallControlsProps) {
  const { t } = useTranslation();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isTranscriptionOpen, setIsTranscriptionOpen] = useState(false);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      await localParticipant.setScreenShareEnabled(false);
    } else {
      await localParticipant.setScreenShareEnabled(true);
    }
    setIsScreenSharing(!isScreenSharing);
  }, [localParticipant, isScreenSharing]);

  const handleToggleChat = useCallback(() => {
    setIsChatOpen(!isChatOpen);
    onToggleChat?.();
  }, [isChatOpen, onToggleChat]);

  const handleToggleTranscription = useCallback(() => {
    setIsTranscriptionOpen(!isTranscriptionOpen);
    onToggleTranscription?.();
  }, [isTranscriptionOpen, onToggleTranscription]);

  return (
    <div className="videocall-controls">
      <TrackToggle
        source={Track.Source.Microphone}
        className="videocall-controls__btn"
        showIcon={true}
      />

      <TrackToggle
        source={Track.Source.Camera}
        className="videocall-controls__btn"
        showIcon={true}
      />

      <button
        className={`videocall-controls__btn ${isScreenSharing ? 'videocall-controls__btn--active' : ''}`}
        onClick={toggleScreenShare}
        title={t('videocall.screenShare')}
      >
        <ScreenShareIcon />
      </button>

      {showChatButton && (
        <button
          className={`videocall-controls__btn ${isChatOpen ? 'videocall-controls__btn--active' : ''}`}
          onClick={handleToggleChat}
          title={t('videocall.chat')}
        >
          <ChatIcon />
        </button>
      )}

      {showTranscriptionButton && (
        <button
          className={`videocall-controls__btn ${isTranscriptionOpen ? 'videocall-controls__btn--active' : ''}`}
          onClick={handleToggleTranscription}
          title={t('videocall.transcription')}
        >
          <TranscriptionIcon />
        </button>
      )}

      {showRecordButton && (
        <button
          className="videocall-controls__btn"
          title={t('videocall.record')}
          disabled
        >
          <RecordIcon />
        </button>
      )}

      <DisconnectButton className="videocall-controls__btn videocall-controls__btn--danger">
        <LeaveIcon />
      </DisconnectButton>
    </div>
  );
}

// Simple SVG icons
function ScreenShareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" />
      <polyline points="8 21 12 17 16 21" />
      <line x1="17" y1="8" x2="17" y2="1" />
      <polyline points="21 5 17 1 13 5" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function TranscriptionIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function RecordIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>
  );
}

function LeaveIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
